import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Grid, Button, Dialog, DialogTitle, DialogContent, Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { enqueueSnackbar } from 'notistack';
import { getCategoryData, getFacilityVendors, getRentalItems, getResidentsForFacility } from '../../../redux/slices/rentals';
import { getCompaniesAndFacilities, getResidents } from '../../../api/rentals';
import { useSelector, useDispatch } from '../../../redux/store';
import { useScopeCheck } from '../../../reusable-components/scopes';
import Iconify from '../../../reusable-components/iconify';
import { getOrderingFacilities } from '../../../redux/slices/orderSheet';
import AccountInformation from '../../../components/dashboard/rentals/newRental/AccountInformation';
import ResidentInformation from '../../../components/dashboard/rentals/newRental/ResidentInformation';
import CategorySelect from '../../../components/dashboard/rentals/newRental/CategorySelect';
import {getUsersRentalInfoForFacility} from '../../../api/home';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';
import LoadingAnimation from '../../../reusable-components/loader/LoadingAnimation';

NewRental.propTypes = {
  setOpenRentalDialog: PropTypes.func,
  openRentalDialog: PropTypes.bool,
  setOpenNewOrderDialog: PropTypes.func,
  isExistingOrder: PropTypes.bool
};

export default function 
NewRental({
  setOpenRentalDialog,
  openRentalDialog,
  setOpenNewOrderDialog,
  isExistingOrder = false
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [facility, setFacility] = useState({});
  const [company, setCompany] = useState({});
  const [resident, setResident] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [facilityResidents, setFacilityResidents] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);
  const [allCompanies, setAllCompanies] = useState(null);
  const [allFacilityVendors, setAllFacilityVendors] = useState(null);
  const [allItems, setAllItems] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [date, setDate] = useState(new Date());
  const [showBlock, setShowBlock] = useState(false);
  const [loadingResidents, setLoadingResidents] = useState(false);
  const canSubmitRentals = useScopeCheck(['Rental-Order-Submit'], true);
  const {
    data: { categoryData, items, facilityVendors},
  } = useSelector((state) => state.rentals);
//get all data not filtered so background doesn't change when selecting data
  useEffect(() => {
    if(!canSubmitRentals) {
      getUsersRentalInfoForFacility()
        .then((response) => {
          if (response === null) {
            setShowBlock(true);
            return;
          }
          setFacilityResidents(response.data?.residents ?? []);
          setAllFacilities(response.data?.facility ? [response.data.facility] : []);
          setAllCompanies(response.data?.company ? [response.data.company] : []);
        })
        .catch(() => enqueueSnackbar("Failed to load facility data", { variant: "error" }));
        return;
    }
    dispatch(getRentalItems());
    dispatch(getCategoryData()); 
    dispatch(getFacilityVendors());
    getCompaniesAndFacilities()
      .then((response) => {
        setAllCompanies(response.companies);
        setAllFacilities(response.facilities);
      })
      .catch(() => enqueueSnackbar("Failed to load facility data", { variant: "error" }));   
  }, []);

  useEffect(() => {
    if  (facilityVendors && allFacilityVendors === null || facilityVendors?.length !== allFacilityVendors?.length) {
        setAllFacilityVendors(facilityVendors);
    }
  }, [facilityVendors, allFacilityVendors]);

  useEffect(() => {
    if (items && (allItems === null || allItems.length !== items.length)) {
        setAllItems(items);
    }
  }, [allItems, items]);

  useEffect(() => {
    setResident(null);
    setFacilityResidents([]);
    if (facility?.id) {
      setLoadingResidents(true);
      getResidents(facility.id)
        .then((response) => setFacilityResidents(response.data ?? []))
        .catch(() => enqueueSnackbar("Failed to load resident data", { variant: "error" }))
        .finally(() => setLoadingResidents(false));
    }
  }, [facility]);

  useEffect(() => {
    if (allItems && allFacilityVendors && facility) {
      const listCategories = allItems
        .filter((item) => allFacilityVendors.some((fv) => fv.vendorId === item.vendorId && fv.facilityId === facility.id))
        .map((item) => ({
          categoryName: item?.categoryName,
          categoryId: item?.categoryId,
          categoryIcon: categoryData.find(x => x.categoryId === item.categoryId)?.categoryIcon
        }));
      const options = listCategories.reduce((acc, item) => {
        if (!acc.some(c => c.label === item.categoryName)) {
          acc.push({
            label: item.categoryName,
            icon: item.categoryIcon,
          })
        }
        return acc;
      }, []);
      setCategoryOptions(options);
    }
  }, [allFacilityVendors, allItems, facility]);
  
  const noPermissionAlert = (
    <Alert
      icon={<LockOutlinedIcon sx={{ color: '#9ccb7a' }} />}
      severity="success"
      sx={{
        mt: 4,
        mb: 2,
        mx: 'auto',
        maxWidth: 520,
        bgcolor: '#f7fbf3',
        border: '1px solid #e4efd9',
        color: '#333',
        textAlign: 'left',
        '& .MuiAlert-message': {
          fontSize: 16,
          lineHeight: 1.4,
        },
      }}
    >
      <strong>You don’t have permission to submit a rental order.</strong>
      <br />
      Reach out to your administrator to gain access.
    </Alert>
  );
    const handleNext = () => {
      setActiveStep((prevStep) => prevStep + 1);
    };
    const handleBack = () => {
      setActiveStep((prevStep) => prevStep - 1);
    };
    const renderStepContent = () => {
      switch (activeStep) {
        case 0:
          return <AccountInformation facility={facility} setFacility={setFacility} company={company} setCompany={setCompany} allFacilities={allFacilities} setAllFacilities={setAllFacilities} allCompanies={allCompanies} />
        case 1:
          return loadingResidents ? <LoadingAnimation /> :
            facilityResidents.length === 0 && !canSubmitRentals ? noPermissionAlert : <ResidentInformation resident={resident} setResident={setResident} facility={facility} facilityResidents={facilityResidents} />;
        case 2:
          return (!canSubmitRentals ? noPermissionAlert :<CategorySelect selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} categoryOptions={categoryOptions}/>);
        case 3:
          return isExistingOrder ? 
          <>
            <DialogTitle
              sx={{
                textAlign: 'center',
                color: 'rgba(112, 112, 112, 0.5)',
              }}
            >
              Rental Start Date
            </DialogTitle>
            <DatePicker
              sx={{ display: 'flex', margin: 'auto', width: '50%' }}
              value={date} 
              onChange={(dateValue) => {
                setDate(new Date(dateValue?.getFullYear(), dateValue?.getMonth(), dateValue?.getDate()));
              }}
            />
          </> :  
          <DialogContent
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 150,
            }}
          >
            <Iconify icon="eos-icons:loading" width={34} height={34} />
          </DialogContent>;
        default:
          return (
            <DialogContent
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 150,
              }}
            >
              <Iconify icon="eos-icons:loading" width={34} height={34} />
            </DialogContent>
          );
      }
    };
    const isStepValid = () => {
      switch (activeStep) {
        case 0:
          return facility?.facilityName;
        case 1:
          return resident !== null;
        case 2:
          return selectedCategory !== '';
        case 3:
          return date !== null;
        default:
          return false;
      }
    };
    const handleCloseRentalDialog = () => {
      setOpenRentalDialog(false);
      setOpen(false);
      setOpenNewOrderDialog(false);
    };

    useEffect(() => {
      if ((activeStep === 3 && !isExistingOrder) || (isExistingOrder && activeStep === 4)) {
        handleCloseRentalDialog();
        //filter redux data properly before navigating off the page
        dispatch(getResidentsForFacility(resident.facilityId));
        dispatch(getFacilityVendors(resident.facilityId));
        navigate(
          `/dashboard/rentals/new-rental?facilityId=${resident.facilityId}&category=${selectedCategory}&residentId=${resident.id}&isExistingOrder=${isExistingOrder}&date=${date.toISOString()}`
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, facility?.value, navigate]);

    useEffect(() => {
      if (open && !allFacilities?.length) dispatch(getOrderingFacilities());
    }, [dispatch, allFacilities, open]);


    return (
      <>
        <Dialog
          open={openRentalDialog}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            handleCloseRentalDialog();
          }}
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'center',
              color: '#8cc53f',
              fontSize: '24px',
              mb: -3,
              fontWeight: 'bold',
            }}
          >
            <div>
              {/* back arrow */}
              {activeStep !== 0 && (
                <Button title="Back" onClick={handleBack} style={{ color: 'gray', maxWidth: '20' }}>
                  <Iconify icon="ic:baseline-arrow-back" width={20} height={20} />
                </Button>
              )}
            </div>
            <CloseIconButton onClick={handleCloseRentalDialog}/>
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: '20px' }}>New Rental</div>
            {activeStep !== 0 && (
              <div style={{ marginLeft: '60px' }}>
                {' '}
                {/* Add an empty div to maintain spacing on the right if needed */}
              </div>
            )}
          </DialogTitle>
          <DialogContent>
            {!showBlock ? renderStepContent() : noPermissionAlert}

            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Button
                title="Next"
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid()}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '50%', mt: 2 }}
              >
                Next
              </Button>
            </Grid>
          </DialogContent>
        </Dialog>
      </>
    );
}
